import {
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useNavigate } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import { VisibilityOff, Visibility, Password } from "@mui/icons-material";
import React from "react";
import { STORAGE_KEYS } from "../../constants";
import { getFromStorage, removeFromStorage, setToStorage } from "../../helpers";
import { useFormik } from "formik";
import * as Yup from "yup";
import { usePostLogInMutation } from "../../service/auth";
import { showError, showToast } from "../../constants/toasts";
import { useAppDispatch } from "../../hooks/store";
import { setCredentials } from "../../reducers/authSlice";
import Loader from "../../constants/Loader";
import { isValidInput } from "../../utils/validations";

const Login = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = React.useState(false);
  const [loginApi, { isLoading }] = usePostLogInMutation();
  const dispatch = useAppDispatch();
  const rawCred = getFromStorage(STORAGE_KEYS.credentials);

  const savedCred =
    typeof rawCred === "object" && rawCred !== null && "email" in rawCred
      ? (rawCred as { email: string; password: string })
      : { email: "", password: "" };

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const formik = useFormik({
    initialValues: {
      email: savedCred?.email,
      password: savedCred?.password,
      remember:
        savedCred?.email?.length && savedCred?.password?.length ? true : false,
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Enter a valid email address")
        .required("Email is required"),
      password: Yup.string()
        .min(8, "Password must be at least 8 characters")
        .required("Password is required"),
    }),
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      try {
        setSubmitting(true);

        //  Simulated login call (replace with API call)
        const { email, password, remember } = values;
        const body = {
          email,
          password,
        };

        const response = await loginApi(body).unwrap();
        console.log(response, "response");
        if (response?.code === 200) {
          showToast(response?.message);
          setToStorage(STORAGE_KEYS.token, response?.data?.token || "");
          dispatch(
            setCredentials({
              user: response?.data,
              token: response?.data?.token || "",
            })
          );

          if (remember) {
            setToStorage(STORAGE_KEYS.credentials, {
              email,
              password,
            });
          } else {
            removeFromStorage(STORAGE_KEYS.credentials);
          }
          navigate("/dashboard");
        }
      } catch (error: any) {
        console.error("Login failed:", error);
        showError(error?.data?.message || "Something went wrong");
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <AuthLayout>
      <div className="authBox">
        <h2>Login</h2>

        <form className="form" onSubmit={formik.handleSubmit}>
          <Loader isLoad={isLoading} />
          <div className="control_group">
            <label htmlFor="email">Email Address</label>
            <TextField
              id="email"
              name="email"
              type="email"
              placeholder="Enter Email Address"
              fullWidth
              value={formik.values.email}
              onChange={(e) => {
                const val = e.target.value;
               if (val.includes(" ")) return;
                formik.handleChange(e);
              }}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
          </div>

          <div className="control_group">
            <label htmlFor="password">Password</label>
            <TextField
              id="password"
              name="password"
              placeholder="Enter Password"
              fullWidth
              type={showPassword ? "text" : "password"}
              value={formik.values.password}
              onChange={(e) => {
                const val = e.target.value;
                console.log(val, "valval");
                if (val.includes(" ")) return;
                formik.handleChange(e);
              }}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" className="eye_btn">
                    <IconButton
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <div className="control_group d_flex">
            <div className="remember_box">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.remember}
                    onChange={(e) =>
                      formik.setFieldValue("remember", e.target.checked)
                    }
                    name="remember"
                  />
                }
                name="remember"
                label="Remember me"
              />
            </div>
            <button
              type="button"
              className="anchor_link"
              onClick={() => navigate("/forgot-password")}
            >
              Forgot Password?
            </button>
          </div>

          <div className="form_btn">
            <Button
              type="submit"
              className="btn btn_primary w_100"
              variant="contained"
            >
              Login
            </Button>
          </div>
        </form>
      </div>
    </AuthLayout>
  );
};

export default Login;
